import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Plus, Save, Trash2, X, Pencil } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function DepartmentsAdmin({ onChanged }) {
  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState({}); // {id: draftName}
  const [busy, setBusy] = useState(null);

  const load = async () => {
    const { data } = await api.get("/reference/all");
    const sorted = [...(data.departments || [])].sort((a, b) => a.name.localeCompare(b.name));
    setItems(sorted);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusy("create");
    try {
      await api.post("/departments", { name });
      toast.success(`Added "${name}"`);
      setNewName("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const save = async (id) => {
    const name = (editing[id] || "").trim();
    if (!name) { toast.error("Name is required"); return; }
    setBusy(id);
    try {
      await api.patch(`/departments/${id}`, { name });
      toast.success("Renamed");
      setEditing((e) => { const n = { ...e }; delete n[id]; return n; });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Delete department "${name}"? Any department code will also be removed.`)) return;
    setBusy(id);
    try {
      await api.delete(`/departments/${id}`);
      toast.success("Deleted");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="departments-admin">
      <div>
        <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#C0C0C0]" strokeWidth={2}/> Departments
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          Add, rename or remove departments. Renames cascade to all contractors, notifications, codes and supervisors.
        </p>
      </div>

      <div className="flex gap-2 items-end max-w-md">
        <div className="flex-1 space-y-1.5">
          <label className="pnc-eyebrow">Add a new department</label>
          <Input
            data-testid="dept-new-name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Wyton Alloys North"
            onKeyDown={(e) => { if (e.key === "Enter") create(); }}
            className="bg-[#1E1E1E] border-[#2A2A2A]"
          />
        </div>
        <Button data-testid="dept-create-btn" disabled={busy === "create"} onClick={create} className="gap-1.5 h-11 rounded-xl bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
          <Plus className="w-4 h-4" strokeWidth={2}/> Add
        </Button>
      </div>

      <div className="border border-[#2A2A2A] rounded-lg overflow-hidden bg-[#1E1E1E]">
        <Table data-testid="departments-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 && (
              <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-8">No departments yet.</TableCell></TableRow>
            )}
            {items.map((d) => {
              const isEditing = d.id in editing;
              return (
                <TableRow key={d.id} data-testid={`dept-row-${d.id}`}>
                  <TableCell className="font-medium">
                    {isEditing ? (
                      <Input
                        data-testid={`dept-edit-${d.id}`}
                        value={editing[d.id]}
                        onChange={(e) => setEditing({ ...editing, [d.id]: e.target.value })}
                        onKeyDown={(e) => { if (e.key === "Enter") save(d.id); if (e.key === "Escape") setEditing((x) => { const n = { ...x }; delete n[d.id]; return n; }); }}
                        autoFocus
                        className="max-w-[300px] bg-[#141414] border-[#2A2A2A]"
                      />
                    ) : (
                      <span>{d.name}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {isEditing ? (
                      <>
                        <Button size="sm" data-testid={`dept-save-${d.id}`} onClick={() => save(d.id)} disabled={busy === d.id} className="gap-1.5 bg-[#C0C0C0] hover:bg-[#8A8A8A] text-[#0A0A0A]">
                          <Save className="w-3.5 h-3.5"/> Save
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`dept-cancel-${d.id}`} onClick={() => setEditing((x) => { const n = { ...x }; delete n[d.id]; return n; })}>
                          <X className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" data-testid={`dept-edit-btn-${d.id}`} onClick={() => setEditing({ ...editing, [d.id]: d.name })} className="gap-1.5 border-[#2A2A2A] bg-[#141414] hover:bg-[#0A0A0A]">
                          <Pencil className="w-3.5 h-3.5"/> Rename
                        </Button>
                        <Button size="sm" variant="ghost" data-testid={`dept-del-${d.id}`} aria-label={`Delete ${d.name}`} onClick={() => remove(d.id, d.name)} disabled={busy === d.id} className="text-[#F87171] hover:bg-[#1F0A0A]">
                          <Trash2 className="w-3.5 h-3.5"/>
                        </Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
